import { format, parseISO } from 'date-fns';
import type { WorkflowOrder, WorkflowNewLocation } from '../../../Api/Orders/ordersApi';

// Shared formatters + detail-field list for the order-request grid and both detail
// renderers (drawer + full page). Leaf module: type-only project imports so the
// Orders col-defs can pull formatters without entering the helpers import cycle.

// parseISO (not new Date) so date-only values like desired_install_date render as the
// submitted calendar day instead of shifting a day in timezones behind UTC.
export const formatDate = (value: string | null | undefined, mask = 'MM/dd/yyyy'): string =>
  value ? format(parseISO(value), mask) : '';

export const formatOrderLocations = (order: WorkflowOrder): string => {
  const locs = order.locations || [];
  const newLocs = (order.new_locations || []).map((loc: WorkflowNewLocation) => loc.address);
  return [...locs, ...newLocs].filter(Boolean).join(', ');
};

export const joinNewLocationField = (order: WorkflowOrder, key: 'locationCode' | 'locationName'): string =>
  (order.new_locations || [])
    .map((loc: WorkflowNewLocation) => loc[key])
    .filter(Boolean)
    .join(', ');

// Prefer what the requester typed for NEW locations; for existing-location requests fall
// back to the SF location account's code/name resolved by the API (single-location,
// available once quoting starts — same coverage as the addressInfo columns).
export const orderLocationCode = (order: WorkflowOrder): string =>
  joinNewLocationField(order, 'locationCode') || order.location_code || '';

export const orderLocationName = (order: WorkflowOrder): string =>
  joinNewLocationField(order, 'locationName') || order.location_name || '';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatSiteContact = (contact: any): string => {
  if (!contact) return '';
  if (typeof contact === 'string') return contact;
  const { firstName, lastName, email, phone } = contact;
  return [firstName, lastName, email, phone].filter(Boolean).join(' ');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatIps = (ips: any): string => {
  if (!ips) return '';
  if (typeof ips === 'string') return ips;
  const value = ips.value || ips.option;
  if (value === 'static') {
    if (ips.numberRange) return `Static (${ips.numberRange.min}-${ips.numberRange.max})`;
    if (ips.count) return `Static (Count: ${ips.count})`;
    return 'Static';
  }
  return value === 'dhcp' ? 'DHCP' : value || '';
};

// Change requests were submitted under a second key for most of this year, and those rows still read back.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const orderDeliveryDetails = (advancedFields: any) =>
  advancedFields?.location_delivery_details || advancedFields?.change_request_details;

export type OrderDetailField = { title: string; value: string };

// Key/value pairs for the order-request "Details" box. The first block mirrors the
// grid's default columns (always rendered, blank when empty); the rest are optional
// extras included only when the request carries a value.
export const orderRequestDetailFields = (order: WorkflowOrder): OrderDetailField[] => {
  const af = order.advanced_fields || {};
  const fields: OrderDetailField[] = [
    { title: 'Request Number', value: order.request_number || '' },
    { title: 'Date of Request', value: formatDate(order.created_at) },
    { title: 'Type of Request', value: order.request_type || '' },
    { title: 'Type of Service', value: order.service_type || '' },
    { title: 'Location', value: formatOrderLocations(order) },
    { title: 'Request Status', value: order.request_status || '' },
    { title: 'Preferred Term', value: order.preferred_term || '' },
    { title: 'Desired Install Date', value: formatDate(af.desired_install_date) },
    { title: 'IP Details', value: af.ip_requirements || formatIps(af.ips) },
    { title: 'Diversity Requirements', value: af.diversifying_from || '' },
    { title: 'Handoff Requirement', value: af.handoff_requirement || '' },
    { title: 'Site Contact', value: formatSiteContact(af.site_contact) },
    { title: 'Location and Delivery Details', value: orderDeliveryDetails(af) || '' }
  ];

  const extras: OrderDetailField[] = [
    { title: 'Location Code', value: orderLocationCode(order) },
    { title: 'Location Name', value: orderLocationName(order) },
    { title: 'Existing Circuit Details', value: af.existing_circuit_details || '' },
    { title: 'Details', value: order.details || '' },
    { title: 'Requested By', value: order.requested_by || '' },
    { title: 'Account', value: order.account_name || '' }
  ];

  return [...fields, ...extras.filter((field) => field.value)];
};
